// Renderer del Ticket de Alivio de Caja 80mm.
//
// La misma función se usa desde:
// - lib/printTicket80.js (impresión real)
// - pages/AdminLabelsPage (preview en vivo)

const esc = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const money = (n) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));

/**
 * Datos de ejemplo usados por el preview del admin.
 */
export const SAMPLE_ALIVIO = {
  fecha: new Date().toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }),
  sucursal: "Villa Luro 2",
  caja: "Diaria 139 · 2026-08-18 10:44:25",
  usuario: "encargado",
  comentario: "Jamón crudo super",
  movimiento: "Egreso",
  monto: 17000,
};

/**
 * @param {object} alivio { fecha, sucursal, caja, usuario, comentario, movimiento, monto }
 * @param {object} config — objeto con textos/secciones/fuentes/layout resuelto
 * @param {{autoprint?: boolean, preview?: boolean}} opts
 */
export function renderAlivioCaja80Html(alivio, config, opts = {}) {
  const { autoprint = false, preview = false } = opts;
  const textos = config?.textos || {};
  const sec = config?.secciones || {};
  const fnt = config?.fuentes || {};
  const layout = config?.layout || {};
  const anchoMm = layout.ancho_mm ?? 72;
  const margenMm = layout.margen_mm ?? 2;

  const fechaStr = alivio.fecha || new Date().toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const sucursal = alivio.sucursal || textos.sucursal || "";
  const movimiento = alivio.movimiento || textos.valor_movimiento || "Egreso";

  const showTitulo = sec.titulo !== false;
  const showFecha = sec.fecha !== false;
  const showSucursal = sec.sucursal !== false && sucursal;
  const showCaja = sec.caja !== false && alivio.caja;
  const showUsuario = sec.usuario !== false && alivio.usuario;
  const showComentario = sec.comentario !== false;
  const showMovimiento = sec.movimiento !== false;
  const showMonto = sec.monto !== false;
  const showFirmaU = sec.firma_usuario !== false;
  const showFirmaS = sec.firma_supervisor !== false;

  const filas = [
    showFecha ? { lbl: textos.label_fecha || "Fecha:", val: fechaStr } : null,
    showSucursal ? { lbl: textos.label_sucursal || "Sucursal:", val: sucursal } : null,
    showCaja ? { lbl: textos.label_caja || "Caja:", val: alivio.caja } : null,
    showUsuario ? { lbl: textos.label_usuario || "Usuario:", val: alivio.usuario } : null,
    showComentario ? { lbl: textos.label_comentario || "Comentario:", val: alivio.comentario || "—" } : null,
    showMovimiento ? { lbl: textos.label_movimiento || "Movimiento:", val: movimiento } : null,
    showMonto ? { lbl: textos.label_monto || "Monto:", val: money(alivio.monto || 0), monto: true } : null,
  ].filter(Boolean);

  const style = `
    @page { size: ${anchoMm + margenMm * 2}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      width: ${anchoMm}mm;
      margin: 0;
      padding: ${margenMm}mm ${margenMm}mm ${margenMm * 2}mm ${margenMm}mm;
      font-family: 'Arial Black', 'Helvetica', Arial, sans-serif;
      font-size: ${fnt.base_px ?? 14}px;
      font-weight: 900;
      line-height: 1.35;
      color: #000;
      -webkit-font-smoothing: none;
      -webkit-text-stroke: 0.35px #000;
      ${preview ? "background: #fff;" : ""}
    }
    .titulo {
      text-align: center;
      font-size: ${fnt.titulo_px ?? 18}px;
      font-weight: 900;
      padding: 4px 0;
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
      margin-bottom: 8px;
    }
    table.data { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
    table.data td {
      padding: 3px 0;
      vertical-align: top;
    }
    table.data td.lbl {
      width: 32mm;
      font-weight: 900;
    }
    table.data td.val {
      font-weight: 900;
      word-wrap: break-word;
      overflow-wrap: anywhere;
    }
    table.data td.val.monto {
      font-size: ${fnt.monto_px ?? 16}px;
    }
    .firma {
      margin-top: 14mm;
      border-top: 1.5px dashed #000;
      padding-top: 3px;
      text-align: center;
      font-weight: 900;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  const script = autoprint ? `
    <script>
      window.addEventListener('load', function () {
        setTimeout(function () { window.focus(); window.print(); }, 120);
      });
    </script>
  ` : "";

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8" />
<title>${esc(textos.titulo || "Egreso de caja")}</title>
<style>${style}</style>
</head>
<body>
  ${showTitulo ? `<div class="titulo">${esc(textos.titulo || "Egreso de caja")}</div>` : ""}
  <table class="data">
    ${filas.map((f) => `
      <tr>
        <td class="lbl">${esc(f.lbl)}</td>
        <td class="val ${f.monto ? "monto" : ""}">${esc(f.val)}</td>
      </tr>
    `).join("")}
  </table>
  ${showFirmaU ? `<div class="firma">${esc(textos.firma_usuario || "Firma usuario")}</div>` : ""}
  ${showFirmaS ? `<div class="firma">${esc(textos.firma_supervisor || "Firma supervisor")}</div>` : ""}
  <div style="height: 8mm"></div>
  ${script}
</body></html>`;
}
